import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { useEscapeKey } from "../lib/useEscapeKey";
import Select from "./Select";

type Props = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
};

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const DATE_LABEL_FORMATTER = new Intl.DateTimeFormat("de-AT", {
  day: "2-digit",
  month: "2-digit",
  timeZone: "UTC",
  year: "numeric",
});

const DAY_ARIA_FORMATTER = new Intl.DateTimeFormat("de-AT", {
  day: "numeric",
  month: "long",
  timeZone: "UTC",
  weekday: "long",
  year: "numeric",
});

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, month) => ({
  value: String(month),
  label: new Intl.DateTimeFormat("de-AT", {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(2026, month, 1))),
}));

export default function DateInput({
  value,
  onChange,
  ariaLabel,
  placeholder = "Datum wählen",
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    monthStart(parseIsoDate(value) ?? todayUtc()),
  );
  const ref = useRef<HTMLDivElement>(null);
  const selected = parseIsoDate(value);
  const days = useMemo(() => calendarDays(visibleMonth), [visibleMonth]);
  const yearOptions = useMemo(() => calendarYearOptions(visibleMonth), [visibleMonth]);

  useEffect(() => {
    const parsed = parseIsoDate(value);
    if (parsed) {
      setVisibleMonth(monthStart(parsed));
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent) {
      if (!ref.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open]);

  useEscapeKey(() => setOpen(false), open);

  const selectedLabel = selected ? DATE_LABEL_FORMATTER.format(selected) : null;
  const active = value !== "";

  function toggleOpen() {
    setOpen((current) => !current);
  }

  function handlePointerToggle(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.focus();
    toggleOpen();
  }

  function handleClickToggle(event: ReactMouseEvent<HTMLButtonElement>) {
    if (event.detail === 0) {
      toggleOpen();
    }
  }

  return (
    <div
      ref={ref}
      className={["relative min-w-0", className ?? ""].join(" ").trim()}
    >
      <div
        className={[
          "flex h-9 min-w-0 items-center rounded-md border bg-white text-sm shadow-sm focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20",
          active ? "border-emerald-400" : "border-slate-300",
        ].join(" ")}
      >
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={ariaLabel}
          onPointerDown={handlePointerToggle}
          onClick={handleClickToggle}
          className="min-w-0 flex-1 truncate px-2 text-left text-slate-900 outline-none"
        >
          {selectedLabel ?? (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </button>
        {active ? (
          <button
            type="button"
            aria-label={`${ariaLabel} löschen`}
            onClick={() => onChange("")}
            className="mr-1 rounded px-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ×
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          role="dialog"
          aria-label={`${ariaLabel} Kalender`}
          className="relative z-40 mt-1 w-full max-w-[19rem] rounded-lg border border-slate-200 bg-white p-3 shadow-lg sm:absolute sm:left-0 sm:w-[19rem]"
        >
          <div className="mb-2 grid grid-cols-[minmax(0,1fr)_4.75rem] gap-1.5 sm:flex sm:items-center">
            <Select
              size="sm"
              aria-label="Monat wählen"
              value={String(visibleMonth.getUTCMonth())}
              onChange={(event) =>
                setVisibleMonth(
                  new Date(
                    Date.UTC(
                      visibleMonth.getUTCFullYear(),
                      Number(event.target.value),
                      1,
                    ),
                  ),
                )
              }
              containerClassName="min-w-0 sm:flex-1"
              className="capitalize"
            >
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select
              size="sm"
              aria-label="Jahr wählen"
              value={String(visibleMonth.getUTCFullYear())}
              onChange={(event) =>
                setVisibleMonth(
                  new Date(
                    Date.UTC(
                      Number(event.target.value),
                      visibleMonth.getUTCMonth(),
                      1,
                    ),
                  ),
                )
              }
              containerClassName="w-[4.75rem] shrink-0"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
            <button
              type="button"
              tabIndex={0}
              aria-label="Vorheriger Monat"
              onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))}
              className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50 sm:w-auto"
            >
              ‹
            </button>
            <button
              type="button"
              tabIndex={0}
              aria-label="Nächster Monat"
              onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
              className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50 sm:w-auto"
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {WEEKDAY_LABELS.map((label, index) => {
              const weekend = index >= 5;
              return (
                <div
                  key={label}
                  data-testid="date-input-weekday"
                  data-weekend={weekend ? "true" : "false"}
                  className={[
                    "py-1 text-[11px] font-semibold uppercase tracking-wide",
                    weekend ? "text-rose-500" : "text-slate-500",
                  ].join(" ")}
                >
                  {label}
                </div>
              );
            })}
            {days.map((day) => {
              const iso = toIsoDate(day);
              const inMonth = day.getUTCMonth() === visibleMonth.getUTCMonth();
              const isSelected = iso === value;
              const isToday = iso === toIsoDate(todayUtc());
              const isWeekend = day.getUTCDay() === 0 || day.getUTCDay() === 6;
              return (
                <button
                  key={iso}
                  type="button"
                  aria-label={DAY_ARIA_FORMATTER.format(day)}
                  aria-pressed={isSelected}
                  data-weekend={isWeekend ? "true" : "false"}
                  onClick={() => {
                    onChange(iso);
                    setOpen(false);
                  }}
                  className={[
                    "h-8 rounded-md text-sm tabular-nums",
                    isSelected
                      ? "bg-emerald-100 font-semibold text-emerald-800 ring-1 ring-emerald-200"
                      : weekendToneClass({ inMonth, isWeekend }),
                    isToday && !isSelected
                      ? isWeekend
                        ? "font-semibold"
                        : "font-semibold text-emerald-700"
                      : "",
                  ].join(" ")}
                >
                  {day.getUTCDate()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return parsed;
}

function todayUtc(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

function monthStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addMonths(date: Date, delta: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + delta, 1));
}

function calendarYearOptions(visibleMonth: Date): number[] {
  const currentYear = todayUtc().getUTCFullYear();
  const visibleYear = visibleMonth.getUTCFullYear();
  const firstYear = Math.min(currentYear - 20, visibleYear);
  const lastYear = Math.max(currentYear + 5, visibleYear);
  return Array.from(
    { length: lastYear - firstYear + 1 },
    (_, index) => lastYear - index,
  );
}

function addDays(date: Date, delta: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + delta));
}

function calendarDays(visibleMonth: Date): Date[] {
  const firstDay = visibleMonth.getUTCDay();
  const mondayBasedOffset = (firstDay + 6) % 7;
  const start = addDays(visibleMonth, -mondayBasedOffset);
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

function toIsoDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function weekendToneClass({
  inMonth,
  isWeekend,
}: {
  inMonth: boolean;
  isWeekend: boolean;
}): string {
  if (isWeekend) {
    return inMonth
      ? "text-rose-600 hover:bg-slate-100"
      : "text-rose-300 hover:bg-slate-100";
  }
  return inMonth ? "text-slate-800 hover:bg-slate-100" : "text-slate-300 hover:bg-slate-100";
}
